import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { CheckCircle, AlertCircle } from 'lucide-react';
import { API_ENDPOINTS } from '../../../utils/config';

const ClientModal = ({ isOpen, onClose, client, onSave, mode = 'add' }) => {
  const [formData, setFormData] = useState({
    name: '',
    branch: '',
    email: '',
    phone: '',
    contact_person: '',
    address: '',
    gst_number: '',
    bank_vertical: '',
    bank_request_id: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (client && mode === 'edit') {
      setFormData({
        name: client?.name || '',
        branch: client?.branch || '',
        email: client?.email || '',
        phone: client?.phone || '',
        contact_person: client?.contact_person || '',
        address: client?.address || '',
        gst_number: client?.gst_number || '',
        bank_vertical: client?.bank_vertical || '',
        bank_request_id: client?.bank_request_id || '',
        client_code: client?.client_code || ''
      });
    } else {
      setFormData({
        name: '',
        branch: '',
        email: '',
        phone: '',
        contact_person: '',
        address: '',
        gst_number: '',
        bank_vertical: '',
        bank_request_id: ''
      });
    }
    setErrors({});
    setSuccessMessage('');
    setErrorMessage('');
  }, [client, mode, isOpen]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = 'Client name is required';
    }

    if (formData?.email?.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.branch?.trim()) {
      newErrors.branch = 'Branch is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');
    setSuccessMessage('');
    
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      };

      const payload = Object.fromEntries(
        Object.entries(formData).filter(([_, v]) => v !== '' && v !== null && v !== undefined)
      );

      if (mode === 'add') {
        const response = await axios.post(
          API_ENDPOINTS.CLIENTS_CREATE,
          payload,
          { headers }
        );
        
        setSuccessMessage('Client created successfully!');
        setTimeout(() => {
          setSuccessMessage('');
          onSave(response.data);
          onClose();
        }, 2000);
      } else {
        const response = await axios.patch(
          API_ENDPOINTS.CLIENTS_UPDATE(client?.id),
          payload,
          { headers }
        );
        
        setSuccessMessage('Client updated successfully!');
        setTimeout(() => {
          setSuccessMessage('');
          onSave(response.data);
          onClose();
        }, 2000);
      }
    } catch (error) {
      // Error saving client handled silently
      
      // Check for 400 error - typically means email already exists
      if (error?.response?.status === 400) {
        const apiError = error?.response?.data?.detail ||
                        error?.response?.data?.message ||
                        error?.response?.data?.error ||
                        'Failed to save client. Please try again.';
        setErrorMessage(apiError);
      } else {
        const apiError = error?.response?.data?.message || 
                        error?.response?.data?.detail || 
                        error?.response?.data?.error ||
                        error?.message || 
                        'Failed to save client. Please try again.';
        setErrorMessage(apiError);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-card rounded-lg border border-border w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-border flex-shrink-0">
          <div className="flex items-center space-x-2">
            <Icon name="UserPlus" size={20} color="var(--color-primary)" />
            <h2 className="text-xl font-semibold text-foreground">
              {mode === 'edit' ? 'Edit Client' : 'Add New Client'}
            </h2>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Modal Content Form */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="overflow-y-auto flex-1 p-6 space-y-6">
            {/* Success/Error Messages */}
            {successMessage && (
              <div className="p-3 rounded-md border border-green-300 bg-green-50 text-green-700">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  <p className="text-sm font-medium">{successMessage}</p>
                </div>
                <p className="text-xs text-green-600 mt-1">This modal will close automatically in 2 seconds...</p>
              </div>
            )}
            {errorMessage && (
              <div className="p-3 rounded-md border border-red-300 bg-red-50 text-red-700">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-5 h-5" />
                  <p className="text-sm">{errorMessage}</p>
                </div>
              </div>
            )}

            {/* Basic Information */}
            <div>
              <h3 className="text-lg font-medium text-foreground mb-4">Basic Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Client Name"
                  type="text"
                  placeholder="Enter client name (e.g., HDFC)"
                  value={formData?.name}
                  onChange={(e) => handleInputChange('name', e?.target?.value)}
                  error={errors?.name}
                  required
                />
                
                <Input
                  label="Branch"
                  type="text"
                  placeholder="Enter branch (e.g., Mysuru)"
                  value={formData?.branch}
                  onChange={(e) => handleInputChange('branch', e?.target?.value)}
                  error={errors?.branch}
                  required
                />
                
                <Input
                  label="Email Address"
                  type="email"
                  placeholder="client@example.com"
                  value={formData?.email}
                  onChange={(e) => handleInputChange('email', e?.target?.value)}
                  error={errors?.email}
                />
                
                <Input
                  label="Phone Number"
                  type="tel"
                  placeholder="0852225198"
                  value={formData?.phone}
                  onChange={(e) => handleInputChange('phone', e?.target?.value)}
                  error={errors?.phone}
                />

                <Input
                  label="Contact Person"
                  type="text"
                  placeholder="Enter contact person name"
                  value={formData?.contact_person}
                  onChange={(e) => handleInputChange('contact_person', e?.target?.value)}
                  error={errors?.contact_person}
                />

                {mode === 'edit' && (
                  <Input
                    label="Client Code"
                    type="text"
                    value={formData?.client_code}
                    readOnly
                    disabled
                  />
                )}
              </div>
            </div>

            {/* Address Information */}
            <div>
              <h3 className="text-lg font-medium text-foreground mb-4">Address Information</h3>
              <Input
                label="Address"
                type="text"
                placeholder="Enter full address"
                value={formData?.address}
                onChange={(e) => handleInputChange('address', e?.target?.value)}
                error={errors?.address}
              />
            </div>

            {/* Bank Information */}
            <div>
              <h3 className="text-lg font-medium text-foreground mb-4">Bank Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="GST Number"
                  type="text"
                  placeholder="Enter GST number (e.g., 29ABBCS8577K3ZK)"
                  value={formData?.gst_number}
                  onChange={(e) => handleInputChange('gst_number', e?.target?.value)}
                />
                
                <Input
                  label="Bank Vertical"
                  type="text"
                  placeholder="Enter bank vertical (e.g., LAP)"
                  value={formData?.bank_vertical}
                  onChange={(e) => handleInputChange('bank_vertical', e?.target?.value)}
                />

                <Input
                  label="Bank Request ID"
                  type="text"
                  placeholder="Enter bank request ID"
                  value={formData?.bank_request_id}
                  onChange={(e) => handleInputChange('bank_request_id', e?.target?.value)}
                />
              </div>
            </div>
          </div>

          {/* Modal Footer */}
          <div className="flex items-center justify-end space-x-3 p-6 border-t border-border bg-muted/30 flex-shrink-0">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              loading={isSubmitting}
              disabled={!!successMessage}
              iconName={successMessage ? "CheckCircle" : "Save"}
              iconPosition="left"
            >
              {successMessage 
                ? 'Success!' 
                : (mode === 'edit' ? 'Update Client' : 'Add Client')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ClientModal;